import React, { useEffect, useMemo, useRef } from "react";
// ANT DESIGN COMPONENTS
import { Table, notification } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateTerminalAutoScroll } from "../../../redux/monitoring/monitoringSlice";
// CUSTOM HOOKS
import {
  useLastBankMessage,
  useLastLiftMessage,
  useBankDispatch,
  useLastSocketMessage,
} from "../socketMessageState/BankMessagesContext";
// UTILS
import { getTableRowClass, generateExcelAndDownload } from "../../../utils/utils";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import getFaultDisplayName from "../../../utils/faultDisplay";
import { directionDisplayName } from "../../../flatbuffers/types/enumDisplayUtils";
import { msgTypeLabel, resolveValue } from "../../../flatbuffers/decoders/applyDeltaToState";
// INTERFACES
import { TerminalExcelReportInterface } from "../../../interfaces/EdgeManagement.interface";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { ReplayRecord } from "../../../utils/binaryReplayCodec";
import { uuidToString, stringToUuidT, EMPTY_UUID } from "../../../utils/uuid";
import { RootState } from "../../../app/store";
// COMPONENTS
import TerminalTableColumns from "./TerminalTableColumns";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";

export default function TerminalTable({
  bankId,
  liftId,
  socketMessages,
  setSocketMessages,
  filterType,
  liftNameMap = {},
  capturedMessagesRef,
}: {
  bankId: string;
  liftId: string;
  socketMessages: SocketMessageInterface[];
  setSocketMessages: (newValue: SocketMessageInterface[]) => void;
  filterType: "ALL" | "UNIT" | "BANK";
  liftNameMap?: Record<string, string>;
  capturedMessagesRef: React.MutableRefObject<ReplayRecord[]>;
}) {
  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const {
    terminalResetTable,
    terminalDownloadReport,
    terminalAutoScroll,
    terminalShowCarHallCalls,
    terminalIconMode,
    stopSocketConnection,
    binaryCaptureActive,
  } = useSelector((state: RootState) => state.monitoring);

  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  // eslint-disable-next-line arrow-body-style
  const columns = React.useMemo(() => {
    return TerminalTableColumns({
      terminalShowCarHallCalls,
      liftNameMap,
      terminalIconMode,
    });
  }, [terminalShowCarHallCalls, liftNameMap, terminalIconMode]);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const dispatchSocketMessage = useBankDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const launchNotification = () => {
    notification.success({
      message: "Text copied to clipboard",
      placement: "top",
      className: `monitoring__notification__${theme}`,
    });
    dispatch(updateTerminalAutoScroll(false));
  };

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const bankUuid = useMemo(() => (bankId ? stringToUuidT(bankId) : EMPTY_UUID), [bankId]);
  const liftUuid = useMemo(() => (liftId ? stringToUuidT(liftId) : EMPTY_UUID), [liftId]);
  const socketMessageBank: SocketMessageInterface =
    useLastBankMessage(bankUuid) || BLANK_SOCKET_MESSAGE;
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const socketMessageLast = useLastSocketMessage();

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (terminalResetTable) {
      setSocketMessages([]);
    }
  }, [terminalResetTable]);

  useEffect(() => {
    if (!stopSocketConnection) {
      if (socketMessageLift && uuidToString(socketMessageLift.liftId) !== "" && liftId !== "") {
        const copy = structuredClone(socketMessages);
        copy.push(socketMessageLift);
        setSocketMessages(copy);
        if (binaryCaptureActive) {
          capturedMessagesRef.current.push({
            timestamp: Date.now(),
            message: structuredClone(socketMessageLift),
          });
        }
      }
      const scrollBody = tableContainerRef.current?.querySelector(".ant-table-body");
      if (scrollBody && terminalAutoScroll) {
        scrollBody.scrollTop = scrollBody.scrollHeight;
      }
    }
  }, [socketMessageLift]);

  useEffect(() => {
    if (!stopSocketConnection) {
      if (socketMessageBank && uuidToString(socketMessageBank.bankId) !== "" && bankId !== "") {
        const copy = structuredClone(socketMessages);
        copy.push(socketMessageBank);
        setSocketMessages(copy);
        if (binaryCaptureActive) {
          capturedMessagesRef.current.push({
            timestamp: Date.now(),
            message: structuredClone(socketMessageBank),
          });
        }
      }
      const scrollBody = tableContainerRef.current?.querySelector(".ant-table-body");
      if (scrollBody && terminalAutoScroll) {
        scrollBody.scrollTop = scrollBody.scrollHeight;
      }
    }
  }, [socketMessageBank]);

  useEffect(() => {
    if (terminalDownloadReport) {
      // Generate excel data
      const copy = structuredClone(socketMessages);
      const newArr: TerminalExcelReportInterface[] = [];
      copy.map((message: SocketMessageInterface) => {
        newArr.push({
          bank_id: uuidToString(message.bankId),
          lift_id: uuidToString(message.liftId),
          position: message.position >= 0 ? message.position : 0,
          direction: directionDisplayName(message.direction),
          msg_type_value: message.msg.messageType,
          content_type_value: message.msg.contentType,
          msg_type: msgTypeLabel(message.msg.contentType, message.msg.messageType),
          msg_value: resolveValue(message.msg),
          msg_active: message.msg?.active ?? false,
          faults: message.faults ?? false,
          active_faults:
            message.activeFaults?.length > 0
              ? message.activeFaults
                  .map((f) => getFaultDisplayName(f.faultType, f.fault?.message ?? 0))
                  .join("; ")
              : "",
          service_mode: (() => {
            const info = getServiceModeDisplay(
              message.serviceMode?.messageType ?? null,
              message.serviceMode?.message?.message ?? null,
            );
            return info?.displayName ?? "";
          })(),
          connected: message.connected ?? false,
          door_status_front: message.doorStatus?.frontOpen ?? false,
          door_status_rear: message.doorStatus?.rearOpen ?? false,
          car_calls: message.carCalls ? JSON.stringify(message.carCalls).replaceAll(",", "-") : "",
          hall_calls: message.hallCalls
            ? JSON.stringify(message.hallCalls).replaceAll(",", "-")
            : "",
          display_map: message.bitmask ? JSON.stringify(message.bitmask).replaceAll(",", "-") : "",
        });
        return true;
      });
      //
      generateExcelAndDownload(newArr, "replayFile");
    }
  }, [terminalDownloadReport]);

  useEffect(() => {
    if (!stopSocketConnection && filterType === "ALL") {
      if (
        socketMessageLast &&
        uuidToString(socketMessageLast.liftId) !== "" &&
        uuidToString(socketMessageLast.bankId) !== ""
      ) {
        const copy = structuredClone(socketMessages);
        copy.push(socketMessageLast);
        setSocketMessages(copy);
        if (binaryCaptureActive) {
          capturedMessagesRef.current.push({
            timestamp: Date.now(),
            message: structuredClone(socketMessageLast),
          });
        }
      }
      const scrollBody = tableContainerRef.current?.querySelector(".ant-table-body");
      if (scrollBody && terminalAutoScroll) {
        scrollBody.scrollTop = scrollBody.scrollHeight;
      }
    }
  }, [socketMessageLast]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div ref={tableContainerRef}>
      <Table
        rowKey={(_, index) => `terminal-row-${index}`}
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        dataSource={socketMessages}
        columns={columns.filter((col) => col.show !== false)}
        scroll={{ y: 340 }}
        size="small"
        pagination={{
          current: 1,
          pageSize: 999999,
          showSizeChanger: false,
        }}
        style={{
          height: 400,
          cursor: "pointer",
        }}
        className="my-custom-table"
        // eslint-disable-next-line arrow-body-style
        onRow={(record: SocketMessageInterface) => {
          return {
            onClick: () => {
              if (stopSocketConnection) {
                dispatchSocketMessage({
                  type: "UPDATE_MESSAGE",
                  payload: record,
                });
              } else {
                // Notification
                navigator.clipboard
                  .writeText(JSON.stringify(record))
                  .then(() => launchNotification())
                  .catch(() =>
                    notification.error({
                      message: "Error",
                      placement: "top",
                      className: `monitoring__notification__${theme}`,
                    }),
                  );
              }
            },
          };
        }}
      />
    </div>
  );
}
